import { useState, useMemo } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  FileTextIcon,
} from "lucide-react"
import { FileTypeIcon } from "./file-type-icon"
import { formatBytes } from "./helpers"
import type { CoversheetData } from "@/components/coversheet-document"
import type { Contact, CoversheetTemplate } from "./types"
import { FaxPreview } from "./fax-preview"

type ReviewStepProps = {
  toNumbers: string[]
  fromNumber: string
  getNumberName: (num: string) => string
  contacts: Contact[]
  files: File[]
  includeCoversheet: boolean
  coversheetMode: "build" | "upload"
  coversheetFile: File | null
  previewCoversheet: CoversheetData
  coversheet: CoversheetData
  templateSkipsBuilder: boolean
  inlineSubject: string
  inlineMessage: string
  calculatedDate: string
  calculatedPages: string
  activeTemplate: CoversheetTemplate | null
}

export function ReviewStep({
  toNumbers,
  fromNumber,
  getNumberName,
  contacts,
  files,
  includeCoversheet,
  coversheetMode,
  coversheetFile,
  previewCoversheet,
  coversheet,
  templateSkipsBuilder,
  inlineSubject,
  inlineMessage,
  calculatedDate,
  calculatedPages,
  activeTemplate,
}: ReviewStepProps) {
  const [recipientIdx, setRecipientIdx] = useState(0)
  const currentIdx = Math.min(recipientIdx, toNumbers.length - 1)

  // Helper to get contact name from number
  const getContactName = (number: string): string => {
    const contact = contacts.find((c) => c.number === number)
    return contact ? contact.name : number
  }

  // Build the coversheet data for the currently previewed recipient
  const currentRecipientCoversheet = useMemo(() => {
    const base: CoversheetData = {
      ...previewCoversheet,
      date: calculatedDate,
      pages: calculatedPages,
    }
    if (templateSkipsBuilder) {
      base.subject = inlineSubject
      base.message = inlineMessage
    }
    if (toNumbers.length === 0) return base
    const num = toNumbers[currentIdx]
    const contact = contacts.find((c) => c.number === num)
    return {
      ...base,
      toFax: num,
      toName: contact?.name ?? "",
      toCompany: "",
      toPhone: "",
    }
  }, [
    previewCoversheet,
    toNumbers,
    currentIdx,
    contacts,
    calculatedDate,
    calculatedPages,
    templateSkipsBuilder,
    inlineSubject,
    inlineMessage,
  ])

  return (
    <div className="grid grid-cols-[1fr_2fr] gap-6 min-h-[540px]">
      {/* Left: summary details */}
      <div className="space-y-4 overflow-y-auto pr-2">
        {/* Recipients */}
        <div className="rounded-lg border p-3 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Recipients ({toNumbers.length})
          </p>
          <div className="flex flex-wrap items-center gap-1.5">
            {toNumbers.map((num, i) => (
              <Badge
                key={num}
                variant={i === currentIdx ? "default" : "secondary"}
                className={`font-mono text-xs cursor-pointer transition-colors ${
                  i === currentIdx ? "" : "hover:bg-muted"
                }`}
                onClick={() => setRecipientIdx(i)}
              >
                {getContactName(num).includes(num)
                  ? num
                  : `${getContactName(num)} (${num})`}
              </Badge>
            ))}
          </div>
        </div>

        {/* Sender */}
        <div className="rounded-lg border p-3 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Sender
          </p>
          <Badge variant="secondary">
            <span className="font-medium">
              {getNumberName(fromNumber)}
            </span>
            <span className="ml-1.5 font-mono text-muted-foreground">
              {fromNumber}
            </span>
          </Badge>
          {activeTemplate && (
            <p className="text-xs text-muted-foreground">
              Template:{" "}
              <span className="font-medium text-foreground">
                {activeTemplate.name}
              </span>
            </p>
          )}
        </div>

        {/* Attachments */}
        <div className="rounded-lg border p-3 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Attachments ({files.length} file
            {files.length !== 1 ? "s" : ""})
          </p>
          <div className="space-y-1">
            {files.map((file) => (
              <div
                key={file.name + file.size}
                className="flex items-center gap-2 text-sm"
              >
                <FileTypeIcon name={file.name} />
                <span className="truncate">{file.name}</span>
                <span className="text-xs text-muted-foreground ml-auto">
                  {formatBytes(file.size)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Cover sheet details summary */}
        {includeCoversheet && coversheetMode === "build" && (
          <div className="rounded-lg border p-3 space-y-1.5 text-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Cover Sheet Details
            </p>
            {(templateSkipsBuilder
              ? inlineSubject
              : coversheet.subject) && (
              <p>
                <span className="text-muted-foreground">Subject:</span>{" "}
                {templateSkipsBuilder
                  ? inlineSubject
                  : coversheet.subject}
              </p>
            )}
            <p>
              <span className="text-muted-foreground">Priority:</span>{" "}
              <span className="capitalize">{coversheet.priority}</span>
            </p>
            {coversheet.fromName && (
              <p>
                <span className="text-muted-foreground">From:</span>{" "}
                {coversheet.fromName}
                {coversheet.fromCompany &&
                  `, ${coversheet.fromCompany}`}
              </p>
            )}
            <p>
              <span className="text-muted-foreground">Date:</span>{" "}
              {calculatedDate}
            </p>
            <p>
              <span className="text-muted-foreground">Pages:</span>{" "}
              {calculatedPages} (incl. cover)
            </p>
            {(templateSkipsBuilder
              ? inlineMessage
              : coversheet.message) && (
              <p className="text-muted-foreground text-xs line-clamp-3 mt-2">
                {templateSkipsBuilder
                  ? inlineMessage
                  : coversheet.message}
              </p>
            )}
          </div>
        )}

        {/* Uploaded coversheet */}
        {includeCoversheet &&
          coversheetMode === "upload" &&
          coversheetFile && (
            <div className="rounded-lg border p-3 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Cover Sheet (Uploaded)
              </p>
              <div className="flex items-center gap-3">
                <FileTextIcon className="size-5 text-red-500 shrink-0" />
                <div>
                  <p className="text-sm font-medium">
                    {coversheetFile.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatBytes(coversheetFile.size)}
                  </p>
                </div>
              </div>
            </div>
          )}
      </div>

      {/* Right: full fax preview with recipient cycling */}
      <div className="flex flex-col gap-2">
        {/* Recipient cycling bar */}
        {toNumbers.length > 1 && (
          <div className="flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2">
            <p className="text-xs text-muted-foreground flex-1">
              Previewing fax for:{" "}
              <span className="font-mono text-foreground font-medium">
                {getContactName(toNumbers[currentIdx])}
              </span>
              <span className="ml-1.5 text-muted-foreground">
                ({currentIdx + 1} of {toNumbers.length})
              </span>
            </p>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7"
              disabled={recipientIdx <= 0}
              onClick={() =>
                setRecipientIdx((i) => Math.max(0, i - 1))
              }
            >
              <ChevronLeftIcon className="size-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7"
              disabled={recipientIdx >= toNumbers.length - 1}
              onClick={() =>
                setRecipientIdx((i) =>
                  Math.min(toNumbers.length - 1, i + 1)
                )
              }
            >
              <ChevronRightIcon className="size-4" />
            </Button>
          </div>
        )}

        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Fax Preview
        </p>
        <div className="flex-1 rounded-lg border bg-muted/30 overflow-hidden min-h-[540px]">
          <FaxPreview
            coversheetData={
              includeCoversheet && coversheetMode === "build"
                ? currentRecipientCoversheet
                : null
            }
            coversheetFile={
              includeCoversheet && coversheetMode === "upload"
                ? coversheetFile
                : null
            }
            files={files}
          />
        </div>
      </div>
    </div>
  )
}
